import { useState, type ElementType, type KeyboardEvent } from "react";
import {
  CalendarClock,
  CalendarX,
  CheckCircle2,
  Clock3,
  Crown,
  Tag,
  Ticket as TicketIcon,
  Timer,
  User,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Ticket, formatTicketTypeLabel } from "@/types/ticket";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatTitleCase, priorityBucket } from "@sd/lib/ticketStreamUtils";
import {
  formatStatusLabel,
  normalizeStatus,
  statusColors,
} from "@sd/constants/ticketStatuses";
import { useAuth } from "@sd/hooks/useAuth";
import { isExternalSdRole } from "@sd/lib/sdRoles";

interface UserPanelProps {
  ticket: Ticket;
  onTagsChange?: (tags: string[]) => void;
  /** When false, hides the read-only VIP row (regular agents). */
  showVip?: boolean;
  /** Mobile stack below ticket detail — full-width metadata block. */
  stacked?: boolean;
}

const COMMON_TAGS = [
  "urgent",
  "follow-up",
  "waiting-approval",
  "needs-review",
  "hardware",
  "software",
  "network",
  "security",
];

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

/** Elapsed time from ticket creation until resolution / close (for closed tickets). */
function formatResolutionDuration(
  fromIso: string,
  toIso: string
): string | null {
  const start = new Date(fromIso).getTime();
  const end = new Date(toIso).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) {
    return null;
  }
  const totalMin = Math.floor((end - start) / 60_000);
  const days = Math.floor(totalMin / (60 * 24));
  const hours = Math.floor((totalMin % (60 * 24)) / 60);
  const mins = totalMin % 60;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0 || days > 0) parts.push(`${hours}h`);
  parts.push(`${mins}m`);
  return parts.join(" ");
}

function resolutionEndInstantIso(ticket: Ticket): string | undefined {
  return (
    ticket.resolvedAt ??
    ticket.resolution?.resolvedAt ??
    ticket.closedAt ??
    ticket.updatedAt
  );
}

const priorityBadgeClass: Record<string, string> = {
  P1: "border-red-200 bg-red-50 text-red-700 font-bold",
  P2: "border-orange-200 bg-orange-50 text-orange-700 font-bold",
  P3: "border-blue-200 bg-blue-50 text-blue-700 font-bold",
  P4: "border-slate-200 bg-slate-100 text-slate-600 font-bold",
};

const priorityDotClass: Record<string, string> = {
  P1: "bg-red-500",
  P2: "bg-orange-500",
  P3: "bg-blue-500",
  P4: "bg-slate-400",
};

function SectionHeading({
  label,
  icon: Icon,
}: {
  label: string;
  icon?: ElementType;
}) {
  return (
    <div className="-mx-1 mb-2 flex items-center gap-2 border-b border-border px-1 py-1 pb-2">
      {Icon && <Icon className="h-4 w-4 text-slate-700" />}
      <h4 className="text-sm font-black uppercase tracking-wide text-slate-900">
        {label}
      </h4>
    </div>
  );
}

function InfoRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5 py-1.5">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <div className="min-w-0 break-words">{children}</div>
    </div>
  );
}

export function UserPanel({
  ticket,
  onTagsChange,
  showVip = true,
  stacked = false,
}: UserPanelProps) {
  const { role } = useAuth();
  const hideAssigneeRequester = isExternalSdRole(role);
  const { user } = ticket;
  const created = formatDateTime(ticket.createdAt);
  const updated = formatDateTime(ticket.updatedAt);
  const tags = ticket.tags ?? [];
  const [tagDraft, setTagDraft] = useState("");

  const typeLabel = formatTicketTypeLabel(ticket.ticketType, ticket.ticketTypeRaw);
  const category = ticket.category?.trim();
  const statusKey = normalizeStatus(ticket.status);
  const statusBadgeClass =
    statusColors[statusKey] ?? "bg-muted text-muted-foreground";
  const statusText = formatStatusLabel(statusKey);

  const isClosedStatus = statusKey === "close";
  const isFixedStatus = statusKey === "ready_for_qa";

  const resolvedAtIso =
    ticket.resolvedAt ??
    ticket.resolution?.resolvedAt ??
    (isFixedStatus ? ticket.updatedAt : undefined);
  const closedAtDisplay =
    resolvedAtIso && (isFixedStatus || isClosedStatus)
      ? formatDateTime(resolvedAtIso)
      : null;

  const closedDateIso =
    ticket.closedAt ?? (isClosedStatus ? ticket.updatedAt : undefined);
  const closedDateDisplay =
    closedDateIso && isClosedStatus ? formatDateTime(closedDateIso) : null;

  const resolutionEndIso = isClosedStatus
    ? resolutionEndInstantIso(ticket)
    : undefined;
  const resolutionDuration =
    resolutionEndIso && ticket.createdAt
      ? formatResolutionDuration(ticket.createdAt, resolutionEndIso)
      : null;

  const canEditTags = Boolean(onTagsChange);

  /** UUID jaisi string ko display value nahi maante */
  const looksLikeUuid = (s?: string | null) =>
    !!s && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s.trim());

  const rawName  = user.name  && !looksLikeUuid(user.name)  ? user.name  : null;
  const rawEmail = user.email && !looksLikeUuid(user.email) ? user.email : null;
  const requesterName    = rawName ?? rawEmail ?? null;
  const requesterInitial = requesterName ? requesterName.charAt(0).toUpperCase() : null;

  const commitTag = (raw: string) => {
    if (!onTagsChange) return;
    const next = raw.trim().toLowerCase();
    if (!next || tags.includes(next)) return;
    onTagsChange([...tags, next]);
    setTagDraft("");
  };

  const removeTag = (tag: string) => {
    if (!onTagsChange) return;
    onTagsChange(tags.filter(t => t !== tag));
  };

  const onTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitTag(tagDraft);
    }
  };

  return (
    <div
      className={cn(
        "relative z-[1] flex w-full shrink-0 flex-col border-t border-border bg-card shadow-sm",
        stacked
          ? "h-auto max-h-none border-t"
          : "h-auto max-h-[42vh] xl:h-full xl:max-h-none xl:w-72 xl:shrink-0 2xl:w-80 xl:border-l xl:border-t-0"
      )}
    >
      <div className="flex h-14 min-h-14 shrink-0 items-center gap-2 border-b border-border bg-slate-100/50 px-4">
        <TicketIcon className="h-5 w-5 shrink-0 text-slate-800" />
        <span className="text-sm font-black uppercase tracking-wide text-slate-900">
          Ticket Details
        </span>
      </div>

      {isClosedStatus && (
        <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-slate-100 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-slate-600" />
            <span className="text-xs font-bold uppercase tracking-widest text-slate-700">
              Ticket Closed
            </span>
          </div>
          <Badge
            variant="outline"
            className="border-slate-200 bg-white text-xs font-semibold text-slate-600"
          >
            {statusText}
          </Badge>
        </div>
      )}

      <ScrollArea className={cn("flex-1", stacked && "max-h-none flex-none")}>
        <div className="space-y-6 p-4">
          <div className="space-y-4">
            <div className="divide-y divide-border/60 overflow-hidden rounded-lg border border-border/80 bg-background shadow-sm">
              {!isClosedStatus && (
                <div className="px-3.5 py-2.5">
                  <InfoRow label="Status">
                    <Badge
                      variant="outline"
                      className={cn(
                        "w-fit max-w-full whitespace-normal rounded-md px-2.5 py-0.5 text-xs font-bold capitalize",
                        statusBadgeClass
                      )}
                    >
                      {statusText}
                    </Badge>
                  </InfoRow>
                </div>
              )}

              <div className="px-3.5 py-2.5">
                <InfoRow label="Ticket Type">
                  <span className="text-sm font-semibold text-slate-900">
                    {typeLabel || "—"}
                  </span>
                </InfoRow>
              </div>

              <div className="px-3.5 py-2.5">
                <InfoRow label="Category">
                  <span className="text-sm font-semibold text-slate-900">
                    {category && category !== "—"
                      ? formatTitleCase(category)
                      : "—"}
                  </span>
                </InfoRow>
              </div>

              <div className="px-3.5 py-2.5">
                <InfoRow label="Priority">
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    <span
                      className={cn(
                        "h-2.5 w-2.5 shrink-0 rounded-full",
                        priorityDotClass[ticket.priority] ?? priorityDotClass.P3
                      )}
                    />
                    <Badge
                      variant="outline"
                      className={cn(
                        "whitespace-normal rounded-md px-2.5 py-0.5 text-xs font-bold",
                        priorityBadgeClass[ticket.priority] ??
                          priorityBadgeClass.P3
                      )}
                    >
                      {ticket.priorityLabel ??
                        (ticket.priorityId ? "—" : ticket.priority)}
                    </Badge>
                  </span>
                </InfoRow>
              </div>

              {showVip ? (
                <div className="px-3.5 py-3">
                  <InfoRow label="VIP">
                    {ticket.isVip ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-bold text-amber-800">
                        <Crown className="h-3.5 w-3.5 fill-amber-500/20" />
                        Yes
                      </span>
                    ) : (
                      <span className="text-sm font-medium text-muted-foreground">
                        No
                      </span>
                    )}
                  </InfoRow>
                </div>
              ) : null}
            </div>

            <div className="mt-2 space-y-0 overflow-hidden rounded-lg border border-border/80 bg-background shadow-sm">
              <div className="flex items-start gap-3 border-b border-border/60 px-3.5 py-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <CalendarClock className="h-4 w-4 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Created
                  </p>
                  <p className="mt-1 break-words text-sm font-medium text-slate-900">
                    {created}
                  </p>
                </div>
              </div>

              <div
                className={cn(
                  "flex items-start gap-3 px-3.5 py-3",
                  (closedAtDisplay || isClosedStatus) &&
                    "border-b border-border/60"
                )}
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <Clock3 className="h-4 w-4 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Last Updated
                  </p>
                  <p className="mt-1 break-words text-sm font-medium text-slate-900">
                    {updated}
                  </p>
                </div>
              </div>

              {closedAtDisplay && (
                <div
                  className={cn(
                    "flex items-start gap-3 px-3.5 py-3",
                    isClosedStatus && "border-b border-border/60"
                  )}
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                    <CheckCircle2 className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Closed On
                    </p>
                    <p className="mt-1 break-words text-sm font-medium text-slate-900">
                      {closedAtDisplay}
                    </p>
                  </div>
                </div>
              )}

              {isClosedStatus && (
                <>
                  <div className="flex items-start gap-3 border-b border-border/60 px-3.5 py-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                      <CalendarX className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Closed On
                      </p>
                      <p className="mt-1 break-words text-sm font-medium text-slate-900">
                        {closedDateDisplay ?? "—"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 px-3.5 py-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                      <Timer className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Resolution Time
                      </p>
                      <p className="mt-1 break-words text-sm font-medium text-slate-900">
                        {resolutionDuration ?? "—"}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {hideAssigneeRequester ? null : (
          <div className="space-y-4">
            <SectionHeading label="Requester" icon={User} />
            <div className="flex items-start gap-3 overflow-hidden rounded-lg border border-border/80 bg-background px-3.5 py-3.5 shadow-sm">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                {requesterInitial ?? <User className="h-4 w-4" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="break-all text-sm font-semibold text-slate-900 leading-snug">
                  {requesterName || "—"}
                </p>
                {rawEmail && rawEmail !== requesterName && (
                  <p className="mt-0.5 break-all text-xs font-medium text-muted-foreground leading-snug">
                    {rawEmail}
                  </p>
                )}
                {!requesterName && !rawEmail && user.email && (
                  <p className="mt-0.5 break-all text-xs font-medium text-muted-foreground leading-snug">
                    {user.email}
                  </p>
                )}
              </div>
            </div>
          </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
